import { cancelProductionOrder, cancelWorkOrder } from '@api/work-order';
import useToaster from '@hooks/toaster/useToaster';
import { Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useState } from 'react';

/** What a grid knows about the row somebody is cancelling. */
export interface CancelTarget {
    /** The row's own key, so the grid can show the spinner on the right row. */
    key: string;
    kind: 'order' | 'line';
    code: string;
    /** The production order id for an order, the work order id for a line. */
    id: string;
}

/**
 * Confirm, then cancel a production order or one of its SKU lines.
 *
 * ONE HOOK FOR EVERY GRID THAT OFFERS IT — the brand's Production Orders page and a site's
 * Production tab today. The aftermath is the same on each (the caller re-reads), and the two
 * levels differ only in the verb and in what the reader is told will happen. Said plainly
 * before they confirm: an order takes its open lines with it; a line leaves the order
 * standing. Either way the manufacturer's queue drops the work at once. Two copies of that
 * wording would drift, and the day they did, one screen would promise something the other
 * did not.
 */
export function useCancelProduction(onDone: () => Promise<unknown> | void): {
    requestCancel: (target: CancelTarget) => void;
    cancellingKey: string | null;
} {
    const { showToastError, showToastSuccess } = useToaster();
    const [cancellingKey, setCancellingKey] = useState<string | null>(null);

    const requestCancel = (target: CancelTarget) => {
        const isOrder = target.kind === 'order';
        modals.openConfirmModal({
            title: `Cancel ${target.code}?`,
            children: (
                <Text size="sm">
                    {isOrder
                        ? 'Every SKU line on it that has not shipped is cancelled with it and its material allocations are cleared. The manufacturer stops seeing the order immediately. Purchase orders already raised for it stay in the system and arrive as un-pegged supply.'
                        : 'Its material allocations are cleared and the manufacturer stops seeing this line immediately. The order itself stays open. Purchase orders already raised for it stay in the system and arrive as un-pegged supply.'}
                </Text>
            ),
            labels: { confirm: isOrder ? 'Cancel order' : 'Cancel line', cancel: 'Keep' },
            confirmProps: { color: 'red' },
            onConfirm: async () => {
                setCancellingKey(target.key);
                try {
                    if (isOrder) await cancelProductionOrder(target.id);
                    else await cancelWorkOrder(target.id);
                    showToastSuccess({ title: `${target.code} cancelled` });
                    await onDone();
                } catch (err: any) {
                    showToastError({
                        title: `Could not cancel ${target.code}`,
                        subtitle: err?.error?.message ?? err?.message ?? 'Something went wrong.',
                    });
                } finally {
                    setCancellingKey(null);
                }
            },
        });
    };

    return { requestCancel, cancellingKey };
}
